/**
 * QuickActionsBarDialog Component
 *
 * Quick actions bar for use in Connections Dialog.
 * Provides actions for connected databases - opens tabs and optionally closes the dialog.
 */

import {
  ArrowDownUp,
  FileCode,
  FolderTree,
  Table,
  Settings,
  Network,
  Terminal,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { isBrowserMode } from '../../browser-mode';
import { useConnectionStore } from '../store/connectionStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useTerminalStore } from '../../terminal';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { revealInObjectExplorer } from '../../explorer/utils/revealInObjectExplorer';
import { createConnectionNodeId } from '../../explorer/utils/nodeIdUtils';
import { openTransfersPanel } from '../../transfers/services/openTransfersPanel';
import { safeConnectionAlias, safeConnectionName, safeConnectionServer } from '@/types/safeConnectionDisplay';

type QuickActionsBarDialogProps = {
  connectionId: string;
  database?: string;
  onClose?: () => void;
};

type ActionButtonProps = {
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  hidden?: boolean;
};

function ActionButton({
  icon,
  label,
  tooltip,
  onClick,
  disabled = false,
  hidden = false,
}: ActionButtonProps) {
  if (hidden) return null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 gap-1 active:scale-[0.96] transition-[color,box-shadow,scale]"
            onClick={onClick}
            disabled={disabled}
            data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {icon}
            <span className="text-xs">{label}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{disabled ? `Connect to enable ${label}` : tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function getConnectionInfo(connectionId: string, database?: string) {
  const { connections, activeConnections } = useConnectionStore.getState();
  const savedConnection = connections.find((c) => c.id === connectionId);
  const activeConnection = activeConnections.find((c) => c.id === connectionId);

  if (!savedConnection && !activeConnection) return null;

  const connectionConfig = savedConnection || activeConnection!.connection;

  return {
    connectionId,
    connectionName: safeConnectionName(connectionConfig, 'Unnamed'),
    serverHost: safeConnectionServer(connectionConfig),
    database: database || activeConnection?.connection.database,
  };
}

export function QuickActionsBarDialog({
  connectionId,
  database,
  onClose,
}: QuickActionsBarDialogProps) {
  // Get the connection to determine engine and default database
  const { activeConnections } = useConnectionStore.getState();
  const activeConnection = activeConnections.find((c) => c.id === connectionId);
  const engine = activeConnection?.connection.engine ?? 'mssql';
  const { track } = useAnalytics();

  // Get the selected database from the explorer UI (if user has selected a database in the tree)
  const selectedDatabase = useDatabaseUIStore((state) => state.selectedDatabase);
  const selectedDatabaseConnectionId = useDatabaseUIStore((state) => state.selectedDatabaseConnectionId);

  // Determine effective database - prioritize user selection, then connection config
  const getEffectiveDatabase = (): string => {
    // First try the database from prop
    if (database) return database;

    // Then check if user has selected a database in the explorer for this connection
    if (selectedDatabase && selectedDatabaseConnectionId === connectionId) {
      return selectedDatabase;
    }

    // Then try the database from connection config
    if (activeConnection?.connection.database) return activeConnection.connection.database;

    // Use sensible defaults when no database is specified
    if (engine === 'sqlite') return safeConnectionAlias(activeConnection?.connection, '');
    // For server-based engines, default to their admin databases
    if (engine === 'mssql') return 'master';
    if (engine === 'pgsql') return 'postgres';
    return '';
  };

  const effectiveDatabase = getEffectiveDatabase();
  const hasDatabase = engine === 'sqlite' ? true : !!effectiveDatabase;

  const handleNewQuery = () => {
    const connectionInfo = getConnectionInfo(connectionId, database);
    if (!connectionInfo) return;

    const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
    const queryNumber = getNextQueryNumber();

    addTab({
      title: `Query-${queryNumber}`,
      type: 'query',
      connectionInfo,
    });

    onClose?.();
  };

  const handleTableExplorer = () => {
    if (!hasDatabase) return;

    const connectionInfo = getConnectionInfo(connectionId, effectiveDatabase);
    if (!connectionInfo) return;

    const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';

    const { addTab } = useWorkspaceStore.getState();

    addTab({
      title: `Table Explorer: ${effectiveDatabase}`,
      type: 'table-explorer',
      connectionInfo,
      content: {
        tableName: '',
        schema: defaultSchema,
        database: effectiveDatabase,
        rows: [],
        columns: [],
        limit: 100,
        selectedColumns: '*',
        filters: [],
        sorting: [],
        isLoading: false,
      },
    });

    onClose?.();
  };

  const handleTableDesigner = () => {
    if (!hasDatabase) return;

    const connectionInfo = getConnectionInfo(connectionId, effectiveDatabase);
    if (!connectionInfo) return;

    const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';

    const { addTab } = useWorkspaceStore.getState();

    addTab({
      title: 'New Table',
      type: 'table-designer',
      connectionInfo,
      content: {
        mode: 'create',
        tableName: '',
        schema: defaultSchema,
        database: effectiveDatabase,
        columns: [],
        isDirty: false,
        isLoading: false,
        isApplying: false,
      },
    });

    onClose?.();
  };

  const handleSchemaOverview = () => {
    if (!hasDatabase) return;

    const connectionInfo = getConnectionInfo(connectionId, effectiveDatabase);
    if (!connectionInfo) return;

    const { addTab } = useWorkspaceStore.getState();

    addTab({
      title: `Overview: ${effectiveDatabase}`,
      type: 'schema-overview',
      connectionInfo,
      content: {
        connectionId,
        databaseName: effectiveDatabase,
        filterMode: 'all',
        isLoading: true,
      },
    });

    onClose?.();
  };

  const handleTerminal = () => {
    useTerminalStore.getState().togglePanel();
    onClose?.();
  };

  // Round B U6 (parity with the Home card): the SQL actions all refuse an Azure Storage connection, so it offers the
  // storage ones — show it in the Object Explorer, open Transfers — and the terminal.
  if (engine === 'azurestorage' && activeConnection) {
    return (
      <div className="flex flex-wrap gap-1 pt-2 border-t border-border/50" data-testid="quick-actions-bar-dialog" data-variant="storage">
        <ActionButton
          icon={<FolderTree className="h-3 w-3" />}
          label="Browse"
          tooltip="Show this connection in the Object Explorer"
          onClick={() => {
            track('feature:storage_quick_action', { action: 'reveal_connection', surface: 'connections_dialog' });
            revealInObjectExplorer(activeConnection.connection, createConnectionNodeId(connectionId));
            onClose?.();
          }}
        />
        <ActionButton
          icon={<ArrowDownUp className="h-3 w-3" />}
          label="Transfers"
          tooltip="Open the Transfers panel"
          onClick={() => {
            track('feature:storage_quick_action', { action: 'transfers', surface: 'connections_dialog' });
            openTransfersPanel('connections_dialog');
            onClose?.();
          }}
        />
        <ActionButton icon={<Terminal className="h-3 w-3" />} label="Terminal" tooltip="Open terminal" onClick={handleTerminal} hidden={isBrowserMode()} />
      </div>
    );
  }

  return (
    <div
      className="flex flex-wrap gap-1 pt-2 border-t border-border/50"
      data-testid="quick-actions-bar-dialog"
    >
      <ActionButton
        icon={<FileCode className="h-3 w-3" />}
        label="Query"
        tooltip="Create a new query tab"
        onClick={handleNewQuery}
      />

      <ActionButton
        icon={<Table className="h-3 w-3" />}
        label="Tables"
        tooltip="Browse table data"
        onClick={handleTableExplorer}
        disabled={!hasDatabase}
      />

      <ActionButton
        icon={<Settings className="h-3 w-3" />}
        label="Design"
        tooltip="Create a new table"
        onClick={handleTableDesigner}
        disabled={!hasDatabase}
      />

      <ActionButton
        icon={<Network className="h-3 w-3" />}
        label="Schema"
        tooltip="View database schema"
        onClick={handleSchemaOverview}
        disabled={!hasDatabase}
      />

      <ActionButton
        icon={<Terminal className="h-3 w-3" />}
        label="Terminal"
        tooltip="Open terminal"
        onClick={handleTerminal}
        hidden={isBrowserMode()}
      />
    </div>
  );
}
